import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../hooks/use-toast";
import { suporteService } from "../services/suporte-service";
import { FilaStatus, Message, TicketMessages, aguardandoResposta } from "../types";

// Polling: a conversa em aberto atualiza a cada 2s, a fila a cada 3s. As duas
// pausam com a aba oculta (refetchIntervalInBackground = false, padrão). O
// alerta do título não usa isto: tem o próprio relógio (ver titulo-alerta.tsx).
const MESSAGES_POLL_MS = 2000;
const QUEUE_POLL_MS = 3000;

export const suporteKeys = {
  all: ["suporte"] as const,
  fila: (status: FilaStatus) => [...suporteKeys.all, "fila", status] as const,
  ticket: (id: string) => [...suporteKeys.all, "ticket", id] as const,
  mensagens: (id: string) => [...suporteKeys.all, "mensagens", id] as const,
  historico: (contactId: string) => [...suporteKeys.all, "historico", contactId] as const,
};

const mensagemErro = (error: any) => error?.response?.data?.message || error?.message || "Erro inesperado.";

export function useFila(status: FilaStatus) {
  return useQuery({
    queryKey: suporteKeys.fila(status),
    queryFn: async () => (await suporteService.fila(status)).data,
    refetchInterval: status === "closed" ? false : QUEUE_POLL_MS,
  });
}

// Quantos atendimentos aguardam na fila, para o contador do menu. Usa a mesma
// chave da aba "Fila": com a tela de suporte aberta os dois compartilham o
// cache e o polling de 5s; fora dela, o menu atualiza a cada 30s (e pausa com
// a aba oculta, padrão do react-query).
const CONTADOR_POLL_MS = 30_000;

export function useContagemFila() {
  return useQuery({
    queryKey: suporteKeys.fila("waiting_agent"),
    queryFn: async () => (await suporteService.fila("waiting_agent")).data,
    refetchInterval: CONTADOR_POLL_MS,
    select: (page) => ({ total: page.items.length, temMais: page.next_cursor !== null }),
  });
}

// Atendimentos em andamento com mensagem nova do entregador (ainda sem resposta).
// Mesma chave da aba "Atendendo": compartilha o cache com ela. Não tem polling
// próprio: quem o usa (alerta do título) chama `refetch` no seu relógio.
export function usePendenciasAtendimento() {
  return useQuery({
    queryKey: suporteKeys.fila("open"),
    queryFn: async () => (await suporteService.fila("open")).data,
    select: (page) => page.items.filter(aguardandoResposta).length,
  });
}

export function useTicket(ticketId: string | null) {
  return useQuery({
    queryKey: suporteKeys.ticket(ticketId ?? ""),
    queryFn: async () => (await suporteService.ticket(ticketId!)).data,
    enabled: Boolean(ticketId),
    refetchInterval: QUEUE_POLL_MS,
  });
}

// Busca a conversa inteira a cada poll: um atendimento tem poucas dezenas de
// mensagens, e assim não há cursor nem deduplicação no cliente.
export function useMensagens(ticketId: string | null, closed: boolean) {
  return useQuery({
    queryKey: suporteKeys.mensagens(ticketId ?? ""),
    queryFn: async () => (await suporteService.mensagens(ticketId!)).data,
    enabled: Boolean(ticketId),
    refetchInterval: closed ? false : MESSAGES_POLL_MS,
  });
}

export function useHistorico(contactId: string | null) {
  return useQuery({
    queryKey: suporteKeys.historico(contactId ?? ""),
    queryFn: async () => (await suporteService.historico(contactId!)).data,
    enabled: Boolean(contactId),
  });
}

// A resposta aparece na conversa na hora (otimista), marcada como "enviando";
// o polling seguinte a troca pela versão gravada. Se falhar, sai da conversa.
export function useResponder(ticketId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const key = suporteKeys.mensagens(ticketId);
  return useMutation({
    // O client_msg_id é gerado por envio: se o usuário clicar de novo após um
    // erro de rede, o chatwoot descarta a duplicata.
    mutationFn: ({ body, clientMsgId }: { body: string; clientMsgId: string }) =>
      suporteService.responder(ticketId, body, clientMsgId),
    onMutate: async ({ body, clientMsgId }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const pendente: Message = {
        msg_id: `~${clientMsgId}`, // "~" ordena depois de qualquer ULID
        ticket_id: ticketId,
        sender_type: "agent",
        sender_name: null,
        body,
        created_at: new Date().toISOString(),
        pendente: true,
      };
      queryClient.setQueryData<TicketMessages>(key, (atual) =>
        atual ? { ...atual, items: [...atual.items, pendente] } : atual
      );
      return { pendenteId: pendente.msg_id };
    },
    onError: (error: any, _vars, ctx) => {
      queryClient.setQueryData<TicketMessages>(key, (atual) =>
        atual ? { ...atual, items: atual.items.filter((m) => m.msg_id !== ctx?.pendenteId) } : atual
      );
      toast({ title: "Erro ao enviar mensagem", description: mensagemErro(error), variant: "destructive" });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: suporteKeys.all }),
  });
}

export function useEncerrar() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (ticketId: string) => suporteService.encerrar(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suporteKeys.all });
      toast({ title: "Atendimento encerrado" });
    },
    onError: (error: any) =>
      toast({ title: "Erro ao encerrar", description: mensagemErro(error), variant: "destructive" }),
  });
}

export function useGerarLink() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ telefone, opsEntregador }: { telefone: string; opsEntregador: string }) =>
      suporteService.gerarLink(telefone, opsEntregador),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: suporteKeys.all }),
    onError: (error: any) =>
      toast({ title: "Erro ao gerar link", description: mensagemErro(error), variant: "destructive" }),
  });
}
